// SHACL runner — wraps Oxigraph's SPARQL-driven shape evaluation.
//
// Note (sub-step 6 scope):
//   Oxigraph 0.5.x does not yet ship a built-in SHACL engine. Phase 2a
//   strategy (per WASM doc §10) is to either (a) translate SHACL shapes to
//   SPARQL queries at build time and run them through Oxigraph's query API,
//   or (b) wait for Oxigraph SHACL support upstream.
//
//   For Step 3 we expose the SPARQL-fallback shape: `applyShacl()` accepts
//   a list of shape-derived SPARQL ASK/SELECT queries + a target IRI, runs
//   them against the in-memory store, and returns a structured report.
//   The signature is locked so Studio integration (Step 4) can swap in a
//   true SHACL engine without breaking callers.

import { getOxiWeb, getStore } from "./core-loader"

export interface ShaclQueryRule {
  /** Shape IRI (cogo:LayoutShape, cogo:NavMenuShape, etc.) */
  shape: string
  /** Human-readable focus class name (e.g. "Navigation"). */
  focusClass?: string
  /**
   * SPARQL ASK or SELECT body. ASK returns a single boolean; SELECT returns
   * the violating bindings (each binding is treated as one violation).
   */
  sparql: string
  /** True if this is an ASK query (asserted-true means PASS). */
  ask: boolean
  /** Severity (matches sh:Violation / sh:Warning / sh:Info). */
  severity: "violation" | "warning" | "info"
  /** Human-readable message template (filled by caller post-hoc). */
  message?: string
}

export interface ShaclViolation {
  shape: string
  severity: "violation" | "warning" | "info"
  focusClass?: string
  message?: string
  /** Bound variable values from the SELECT row (empty for ASK rules). */
  bindings: Record<string, string>
}

export interface ShaclReport {
  conforms: boolean
  totalRules: number
  violations: ShaclViolation[]
  durationMs: number
}

export interface DiscoveredRule {
  code: string
  severity: string
  message: string
  sparql: string
}

export interface CogoBlockValidationDiagnostic {
  rule: string
  severity: string
  message: string
}

export interface CogoBlockValidationResult {
  conforms: boolean
  errors: string[]
  warnings: string[]
  diagnostics: CogoBlockValidationDiagnostic[]
  durationMs: number
  rulesEvaluated: number
}

/**
 * Run a set of SHACL-derived SPARQL rules against the loaded core graph.
 * Returns a structured report mirroring the SHACL Conforms / Violations
 * output format.
 */
export function applyShacl(rules: ShaclQueryRule[]): ShaclReport {
  const store = getStore()
  if (!store) {
    throw new Error(
      "[cogo-kg-browser] applyShacl called before initKgCore — store not ready.",
    )
  }
  const t0 = nowMs()
  const violations: ShaclViolation[] = []

  for (const rule of rules) {
    try {
      const out = store.query(rule.sparql)
      if (rule.ask) {
        // ASK returns a boolean. Per SHACL convention, an ASK that returns
        // true means the constraint IS asserted; if the rule is phrased as
        // a forbidden pattern, true == violation. We don't try to invert
        // here — caller defines the polarity in `message`.
        if (out === true) {
          violations.push({
            shape: rule.shape,
            severity: rule.severity,
            focusClass: rule.focusClass,
            message: rule.message,
            bindings: {},
          })
        }
      } else {
        // SELECT — each row is a violation.
        const rows = out as Iterable<Map<string, unknown>>
        for (const row of rows) {
          const bindings: Record<string, string> = {}
          if (row && typeof (row as any)[Symbol.iterator] === "function") {
            for (const [k, v] of row as unknown as Iterable<[string, { value?: string } | null]>) {
              bindings[k] = v?.value ?? "null"
            }
          }
          violations.push({
            shape: rule.shape,
            severity: rule.severity,
            focusClass: rule.focusClass,
            message: rule.message,
            bindings,
          })
        }
      }
    } catch (e) {
      violations.push({
        shape: rule.shape,
        severity: "violation",
        focusClass: rule.focusClass,
        message: `SPARQL evaluation failed: ${(e as Error).message}`,
        bindings: {},
      })
    }
  }

  const conforms = !violations.some((v) => v.severity === "violation")
  return {
    conforms,
    totalRules: rules.length,
    violations,
    durationMs: nowMs() - t0,
  }
}

/**
 * Validate a compiled Turtle RDF representation of a Cogo JSON block against
 * active cogo:Rule entries loaded in the Oxigraph TBox.
 *
 * This is the editor-facing offline validator: callers serialize Cogo JSON to
 * Turtle, this function loads it into a short-lived named graph, discovers
 * SELECT/ASK violation queries from the ontology, and returns compact
 * diagnostics. The return shape keeps the original `{ conforms, errors }`
 * contract while exposing warnings and timing for UI badges.
 */
export function validateCogoBlock(
  block: any,
  rdfTurtle: string,
  graphNode = "https://cogo.xyz/ns/v1#block/sandbox",
): CogoBlockValidationResult {
  const t0 = nowMs()
  const store = getStore()
  if (!store) {
    return finishValidation(fallbackDiagnostics(block), 0, t0)
  }

  const oxi = getOxiWeb()
  const graphIri = escapeGraphIri(graphNode)

  dropGraphSilently(store, graphIri)

  try {
    const quads = oxi.parse ? oxi.parse(rdfTurtle, { format: "text/turtle" }) : []
    for (const q of quads) {
      store.add(oxi.quad(q.subject, q.predicate, q.object, oxi.namedNode(graphIri)))
    }
  } catch (err) {
    dropGraphSilently(store, graphIri)
    return finishValidation(
      [
        {
          rule: "turtle-syntax",
          severity: "error",
          message: `Syntax Error in Turtle compilation: ${(err as Error).message || String(err)}`,
        },
      ],
      0,
      t0,
    )
  }

  const discoveredRules = discoverRules(store)
  const diagnostics: CogoBlockValidationDiagnostic[] = []
  const queryOpts = { use_default_graph_as_union: true }

  for (const rule of discoveredRules) {
    if (!rule.sparql) continue
    try {
      const result = store.query(rule.sparql, queryOpts)
      if (queryHasMatch(result)) {
        diagnostics.push({
          rule: rule.code || "unnamed-rule",
          severity: normalizeSeverity(rule.severity),
          message: rule.message || "Ontology rule violation",
        })
      }
    } catch (err) {
      diagnostics.push({
        rule: rule.code || "sparql-rule",
        severity: "error",
        message: `SPARQL evaluation failed: ${(err as Error).message || String(err)}`,
      })
    }
  }

  diagnostics.push(...fallbackDiagnostics(block))
  dropGraphSilently(store, graphIri)
  return finishValidation(diagnostics, discoveredRules.length, t0)
}

function discoverRules(store: NonNullable<ReturnType<typeof getStore>>): DiscoveredRule[] {
  const rulesQuery = `
    PREFIX cogo: <https://cogo.xyz/ns/v1#>
    SELECT ?code ?severity ?message ?sparql WHERE {
      ?ruleIri a cogo:Rule ;
               cogo:code ?code ;
               cogo:severity ?severity ;
               cogo:message ?message ;
               cogo:sparqlAsk ?sparql .
    }
  `
  const rules: DiscoveredRule[] = []
  try {
    const rows = store.query(rulesQuery) as Iterable<Map<string, { value?: string } | null>>
    for (const row of rows) {
      rules.push({
        code: bindingValue(row, "code"),
        severity: bindingValue(row, "severity"),
        message: bindingValue(row, "message"),
        sparql: bindingValue(row, "sparql"),
      })
    }
  } catch (err) {
    rules.push({
      code: "rules-discovery",
      severity: "error",
      message: `Rules discovery failed: ${(err as Error).message || String(err)}`,
      sparql: "ASK {}",
    })
  }
  return rules
}

function bindingValue(row: Map<string, { value?: string } | null>, key: string): string {
  return row && typeof row.get === "function" ? row.get(key)?.value ?? "" : ""
}

function queryHasMatch(result: unknown): boolean {
  if (typeof result === "boolean") return result
  if (!result) return false
  if (Array.isArray(result)) return result.length > 0
  if (typeof (result as any)[Symbol.iterator] === "function") {
    for (const _row of result as Iterable<unknown>) return true
  }
  return false
}

function fallbackDiagnostics(block: any): CogoBlockValidationDiagnostic[] {
  const diagnostics: CogoBlockValidationDiagnostic[] = []
  if (!block || typeof block !== "object") {
    diagnostics.push({
      rule: "invalid-json",
      severity: "error",
      message: "Block JSON is completely invalid or empty.",
    })
    return diagnostics
  }
  if (!block.id) {
    diagnostics.push({
      rule: "block-name",
      severity: "error",
      message: "Block name must be present and valid.",
    })
  }
  if (!block.type) {
    diagnostics.push({
      rule: "element-type",
      severity: "error",
      message: "Semantic ctype role is missing.",
    })
  }
  return diagnostics
}

function finishValidation(
  diagnostics: CogoBlockValidationDiagnostic[],
  rulesEvaluated: number,
  startMs: number,
): CogoBlockValidationResult {
  const errors = diagnostics
    .filter((d) => !isWarningSeverity(d.severity))
    .map((d) => `Rule: ${d.rule} - ${d.message}`)
  const warnings = diagnostics
    .filter((d) => isWarningSeverity(d.severity))
    .map((d) => `Rule: ${d.rule} - ${d.message}`)
  return {
    conforms: errors.length === 0,
    errors,
    warnings,
    diagnostics,
    durationMs: nowMs() - startMs,
    rulesEvaluated,
  }
}

function normalizeSeverity(severity: string): string {
  const lower = severity.toLowerCase()
  if (lower.includes("warning") || lower === "warn") return "warning"
  if (lower.includes("info")) return "info"
  return lower || "error"
}

function isWarningSeverity(severity: string): boolean {
  const normalized = normalizeSeverity(severity)
  return normalized === "warning" || normalized === "info"
}

function dropGraphSilently(store: NonNullable<ReturnType<typeof getStore>>, graphIri: string): void {
  try {
    store.update(`DROP SILENT GRAPH <${graphIri}>`)
  } catch {
    // Cleanup is best-effort; hydration/evaluation diagnostics carry the user-facing failure.
  }
}

function escapeGraphIri(iri: string): string {
  if (!iri || typeof iri !== "string") {
    throw new Error("[cogo-kg-browser] graph IRI must be a non-empty string.")
  }
  if (iri.includes("<") || iri.includes(">")) {
    throw new Error(`[cogo-kg-browser] refusing graph IRI containing angle brackets: ${iri}`)
  }
  return iri
}

function nowMs(): number {
  const perf = (globalThis as any).performance
  if (perf && typeof perf.now === "function") return perf.now()
  return Date.now()
}
